"use client";

import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { createPublicClient } from "@/lib/hooks";
import { Card, CardContent } from "@/components/ui/card";
import type { video } from "@/api/client";
import { useTranslation } from "react-i18next";

function SharedVideoPage() {
  const { shareId } = useParams<{ shareId: string }>();
  const [video, setVideo] = useState<video.SharedVideoResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { t } = useTranslation();

  useEffect(() => {
    if (!shareId) {
      setError(t("sharedVideo.noShareId"));
      setIsLoading(false);
      return;
    }

    const fetchSharedVideo = async () => {
      try {
        const client = createPublicClient();
        const response = await client.video.getSharedVideo(shareId);
        setVideo(response);
      } catch (err) {
        console.error("Error fetching shared video:", err);
        setError(t("sharedVideo.notFound"));
      } finally {
        setIsLoading(false);
      }
    };

    fetchSharedVideo();
  }, [shareId]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <div className="w-8 h-8 border-2 border-rose-dark border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-muted-foreground">{t("videoDetail.loadingVideo")}</p>
        </div>
      </div>
    );
  }

  if (error || !video) {
    return (
      <div className="min-h-screen bg-lavender flex items-center justify-center">
        <div className="text-center max-w-md px-6">
          <h1 className="font-h2 mb-4">{t("sharedVideo.unavailable")}</h1>
          <p className="text-muted-foreground">
            {error || t("sharedVideo.invalidLink")}
          </p>
        </div>
      </div>
    );
  }

  const createdAt = new Date(video.createdAt).toLocaleDateString("sv-SE", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="min-h-screen bg-lavender">
      <main className="max-w-4xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="font-h2">{video.title}</h1>
          {video.description && (
            <p className="text-muted-foreground mt-2">{video.description}</p>
          )}
          <p className="text-sm text-muted-foreground mt-2">
            {t("common.createdAt", { date: createdAt })}
          </p>
        </div>

        {/* Video player */}
        <Card className="bg-white rounded-md border border-rose-dark shadow-[0_4px_13px_rgba(0,0,0,0.1)]">
          <CardContent className="p-6">
            <video
              src={video.videoUrl}
              controls
              autoPlay
              className="w-full rounded-md"
            />
          </CardContent>
        </Card>

        {/* Branding */}
        <div className="text-center mt-8">
          <p className="text-sm text-muted-foreground">
            {t("sharedVideo.madeWith")}{" "}
            <a href="/" className="text-rose-dark hover:underline">
              Dovie
            </a>
          </p>
        </div>
      </main>
    </div>
  );
}

export { SharedVideoPage };
